import api from "./api";

const paramsOrEmpty = (params) => ({ params: params || {} });

export const portfolioService = {
  create: (payload) => api.post("/api/portfolios", payload),
  list: (params) => api.get("/api/portfolios", paramsOrEmpty(params)),
  getById: (portfolioId) => api.get(`/api/portfolios/${portfolioId}`),
  update: (portfolioId, payload) =>
    api.put(`/api/portfolios/${portfolioId}`, payload),
  remove: (portfolioId) => api.delete(`/api/portfolios/${portfolioId}`),
};

export const programService = {
  create: (payload) => api.post("/api/programs", payload),
  list: (params) => api.get("/api/programs", paramsOrEmpty(params)),
  getById: (programId) => api.get(`/api/programs/${programId}`),
  update: (programId, payload) =>
    api.put(`/api/programs/${programId}`, payload),
  remove: (programId) => api.delete(`/api/programs/${programId}`),
};

export const projectService = {
  create: (payload) => api.post("/api/projects", payload),
  list: (params) => api.get("/api/projects", paramsOrEmpty(params)),
  getById: (projectId) => api.get(`/api/projects/${projectId}`),
  update: (projectId, payload) =>
    api.put(`/api/projects/${projectId}`, payload),
  remove: (projectId) => api.delete(`/api/projects/${projectId}`),
  transition: (projectId, payload) =>
    api.post(`/api/projects/${projectId}/transition`, payload),
  addMember: (projectId, payload) =>
    api.post(`/api/projects/${projectId}/members`, payload),
  removeMember: (projectId, employeeId) =>
    api.delete(`/api/projects/${projectId}/members/${employeeId}`),
  dashboard: (projectId) => api.get(`/api/projects/${projectId}/dashboard`),
  health: (projectId) => api.get(`/api/projects/${projectId}/health`),
  history: (projectId, params) =>
    api.get(`/api/projects/${projectId}/history`, paramsOrEmpty(params)),
  metrics: () => api.get("/api/projects/metrics"),
};

export const sprintService = {
  create: (payload) => api.post("/api/sprints", payload),
  listByProject: (projectId, params) =>
    api.get(`/api/sprints/project/${projectId}`, paramsOrEmpty(params)),
  getActiveByProject: (projectId) =>
    api.get(`/api/sprints/project/${projectId}/active`),
  getById: (sprintId) => api.get(`/api/sprints/${sprintId}`),
  update: (sprintId, payload) => api.put(`/api/sprints/${sprintId}`, payload),
  start: (sprintId) => api.post(`/api/sprints/${sprintId}/start`),
  complete: (sprintId, carryForwardNextSprintId = null) =>
    api.post(`/api/sprints/${sprintId}/complete`, { carryForwardNextSprintId }),
  addTicket: (sprintId, ticketId) =>
    api.post(`/api/sprints/${sprintId}/tickets`, { ticketId }),
  removeTicket: (sprintId, ticketId) =>
    api.delete(`/api/sprints/${sprintId}/tickets/${ticketId}`),
  analytics: (sprintId) => api.get(`/api/sprints/${sprintId}/analytics`),
};

export const milestoneService = {
  create: (payload) => api.post("/api/milestones", payload),
  listByProject: (projectId, params) =>
    api.get(`/api/milestones/project/${projectId}`, paramsOrEmpty(params)),
  getById: (milestoneId) => api.get(`/api/milestones/${milestoneId}`),
  update: (milestoneId, payload) =>
    api.put(`/api/milestones/${milestoneId}`, payload),
  complete: (milestoneId) =>
    api.post(`/api/milestones/${milestoneId}/complete`),
  remove: (milestoneId) => api.delete(`/api/milestones/${milestoneId}`),
};

export const releaseService = {
  create: (payload) => api.post("/api/releases", payload),
  listByProject: (projectId, params) =>
    api.get(`/api/releases/project/${projectId}`, paramsOrEmpty(params)),
  getById: (releaseId) => api.get(`/api/releases/${releaseId}`),
  update: (releaseId, payload) =>
    api.put(`/api/releases/${releaseId}`, payload),
  deploy: (releaseId) => api.post(`/api/releases/${releaseId}/deploy`),
  rollback: (releaseId, reason) =>
    api.post(`/api/releases/${releaseId}/rollback`, { reason }),
  generateNotes: (releaseId) =>
    api.post(`/api/releases/${releaseId}/generate-notes`),
};

export const resourceService = {
  createAllocation: (payload) =>
    api.post("/api/resources/allocations", payload),
  listProjectAllocations: (projectId) =>
    api.get(`/api/resources/allocations/project/${projectId}`),
  listEmployeeAllocations: (employeeId, params) =>
    api.get(
      `/api/resources/allocations/employee/${employeeId}`,
      paramsOrEmpty(params),
    ),
  utilization: (params) =>
    api.get("/api/resources/utilization", paramsOrEmpty(params)),
  updateAllocation: (allocationId, payload) =>
    api.put(`/api/resources/allocations/${allocationId}`, payload),
  deactivateAllocation: (allocationId) =>
    api.delete(`/api/resources/allocations/${allocationId}`),
};

export const timesheetService = {
  createEntry: (payload) => api.post("/api/timesheets", payload),
  listMyEntries: (params) => api.get("/api/timesheets", paramsOrEmpty(params)),
  projectSummary: (projectId) =>
    api.get(`/api/timesheets/project/${projectId}/summary`),
  submitEntry: (entryId) => api.post(`/api/timesheets/${entryId}/submit`),
  approveEntry: (entryId) => api.post(`/api/timesheets/${entryId}/approve`),
  // Aliases used across existing EDOS pages
  log: (payload) => api.post("/api/timesheets", payload),
  myEntries: (params) => api.get("/api/timesheets", paramsOrEmpty(params)),
  submit: (entryId) => api.post(`/api/timesheets/${entryId}/submit`),
  approve: (entryId) => api.post(`/api/timesheets/${entryId}/approve`),
};

export const automationService = {
  create: (payload) => api.post("/api/automations", payload),
  list: (params) => api.get("/api/automations", paramsOrEmpty(params)),
  getById: (automationId) => api.get(`/api/automations/${automationId}`),
  update: (automationId, payload) =>
    api.put(`/api/automations/${automationId}`, payload),
  remove: (automationId) => api.delete(`/api/automations/${automationId}`),
  metadata: () => api.get("/api/automations/triggers"),
};

export const dashboardService = {
  executive: () => api.get("/api/dashboards/executive"),
  projectManager: () => api.get("/api/dashboards/project-manager"),
  developer: () => api.get("/api/dashboards/developer"),
  sprintBoard: (projectId) =>
    api.get(`/api/dashboards/sprint-board/${projectId}`),
  releasePipeline: (projectId) =>
    api.get(`/api/dashboards/release-pipeline/${projectId}`),
};
